import React from 'react';
import { Slider as AntSlider, SliderSingleProps, SliderRangeProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export type SliderProps = (SliderSingleProps | SliderRangeProps) & {
  className?: string;
};

const useStyles = createUseStyles((theme: ThemeType) => ({
  slider: {
    '&.ant-slider': {
      '& .ant-slider-rail': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        height: 4,
        borderRadius: theme.radius.xs,
      },
      '& .ant-slider-track': {
        backgroundColor: theme.color.semantic.brand.default.primary,
        height: 4,
        borderRadius: theme.radius.xs,
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.secondary,
        },
      },
      '& .ant-slider-handle': {
        width: 16,
        height: 16,
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        border: `2px solid ${theme.color.semantic.border.primary}`,
        marginTop: -6,
        '&:hover': {
          borderColor: theme.color.semantic.brand.default.secondary,
        },
        '&:focus': {
          borderColor: theme.color.semantic.brand.default.moderate,
          boxShadow: `0 0 0 3px ${theme.color.semantic.brand.default.moderate}33`,
        },
        '&:active': {
          borderColor: theme.color.semantic.brand.default.moderate,
          boxShadow: `0 0 0 3px ${theme.color.semantic.brand.default.moderate}33`,
        },
        '&.ant-slider-handle-dragging': {
          borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
          boxShadow: `0 0 0 3px ${theme.color.semantic.brand.default.moderate}33 !important`,
        },
      },
      '& .ant-slider-mark-text': {
        color: theme.color.semantic.text.secondary,
        fontSize: theme.typography.fontSize.xs,
      },
      '& .ant-slider-dot': {
        backgroundColor: theme.color.semantic.background.neutral.strong,
        borderColor: theme.color.semantic.border.primary,
        width: 6,
        height: 6,
        marginTop: -1,
        '&.ant-slider-dot-active': {
          backgroundColor: theme.color.semantic.brand.default.primary,
          borderColor: theme.color.semantic.brand.default.primary,
        },
      },
      '&.ant-slider-disabled': {
        '& .ant-slider-rail': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        },
        '& .ant-slider-track': {
          backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
        },
        '& .ant-slider-handle': {
          backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
          borderColor: `${theme.color.semantic.border.strong} !important`,
          cursor: 'not-allowed',
          '&:hover, &:focus, &:active': {
            borderColor: `${theme.color.semantic.border.strong} !important`,
            boxShadow: 'none !important',
          },
        },
        '& .ant-slider-mark-text': {
          color: `${theme.color.semantic.text.soft} !important`,
        },
        '& .ant-slider-dot': {
          backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
          borderColor: `${theme.color.semantic.border.strong} !important`,
        },
      },
      '&.ant-slider-vertical': {
        height: '100%',
        minHeight: 200,
        '& .ant-slider-rail': {
          width: 4,
          height: '100%',
        },
        '& .ant-slider-track': {
          width: 4,
        },
        '& .ant-slider-handle': {
          marginTop: 0,
          marginLeft: -6,
        },
        '& .ant-slider-dot': {
          marginTop: 0,
          marginLeft: -1,
        },
      },
    },
    '& .ant-tooltip': {
      '& .ant-tooltip-inner': {
        backgroundColor: theme.color.semantic.background.neutral.inverse,
        color: theme.color.semantic.text.inverse,
        fontSize: theme.typography.fontSize.xs,
        padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
        minHeight: 'auto',
      },
      '& .ant-tooltip-arrow-content': {
        backgroundColor: theme.color.semantic.background.neutral.inverse,
      },
    },
  },
}));

export const Slider: React.FC<SliderProps> = ({ className, ...props }) => {
  const classes = useStyles();

  return (
    <AntSlider
      {...props}
      className={`${classes.slider} ${className || ''}`}
    />
  );
};

Slider.displayName = 'Slider';

export default Slider;
